import React from "react"
import { CardStat, Divider, EventStatusBadge } from "@components"
import { formattedDateRange, hasMeetingType } from "@helpers"
import { useEventMenu } from "@hooks"
import { Cog8ToothIcon, UsersIcon } from "@heroicons/react/24/outline"
import PropTypes from "prop-types"

const EventSettings = ({
  event,
  maxBookingsPerStudent = 0,
  maxBookingsRestrictedDays = 0,
  meetingTypes = [],
  vcAutoDisconnect = false,
  noOfSessions = 0,
  openToParents = null,
  startsAt,
  endsAt,
  slotDuration = null,
  slotGap = null,
  user,
}) => {
  const { open: openEventMenu } = useEventMenu(user, event)
  const eventDate = startsAt === null ? "No Date Set" : formattedDateRange(startsAt, endsAt, true, "D", "MMM", "YYYY")

  return (
    <React.Fragment>
      <div className="px-4 gap-y-2">
        <h3 className="text-indigo-600 dark:text-foreground text-md">Date of event</h3>

        <div className="flex items-center justify-between">
          <span className="font-semibold text-foreground">{eventDate}</span> 
          
          <div className="hidden lg:block">
            <EventStatusBadge size="md" startDate={startsAt} endDate={endsAt} />
          </div>
        </div>
      </div>

      <Divider className="my-3" />

      <CardStat 
        icon={Cog8ToothIcon} 
        meta="Number of sessions" 
        title="No Of Sessions" 
        stat={noOfSessions} 
        handleClick={() => openEventMenu("SESSIONS")}
      />

      <Divider className="my-3" />

      {meetingTypes.length > 0 && (
        <React.Fragment>
          <CardStat
            icon={Cog8ToothIcon}
            meta={
              meetingTypes.length === 1
                ? meetingTypes[0].name + " only"
                : meetingTypes.map(meetingType => meetingType.name).join(" | ")
            }
            title="Meeting Types"
            stat={meetingTypes.length}
            handleClick={() => openEventMenu("MEETING_TYPES")}
          />

          <Divider className="my-3" />
        </React.Fragment>
      )}

      {meetingTypes.length > 0 && hasMeetingType("VC", meetingTypes) && (
        <React.Fragment>
          <CardStat
            icon={Cog8ToothIcon}
            meta="Automatically disconnect VC"
            title="VC Auto Disconnect"
            stat={vcAutoDisconnect ? "Yes" : "No"}
            handleClick={() => openEventMenu("SETTINGS")}
          />

          <Divider className="my-3" />
        </React.Fragment>
      )}

      {slotDuration && (
        <React.Fragment>
          <CardStat icon={Cog8ToothIcon} meta="Slot duration meta" title="Slot Duration" stat={slotDuration} />
          <Divider className="my-3" />
        </React.Fragment>
      )}

      {slotGap && (
        <React.Fragment>
          <CardStat icon={Cog8ToothIcon} meta="Slot gap meta" title="Slot Gap" stat={slotGap} />
          <Divider className="my-3" />
        </React.Fragment>
      )}

      {openToParents !== null && (
        <React.Fragment>
          <CardStat
            icon={UsersIcon}
            meta="Is this event open to parents"
            title="Open to Parents"
            stat={openToParents ? "Yes" : "No"}
          />

          <Divider className="my-3" />
        </React.Fragment>
      )}

      <CardStat
        icon={Cog8ToothIcon}
        meta={maxBookingsPerStudent === 0 ? "booking(s) per student" : (maxBookingsRestrictedDays === 0) ? 'booking(s) per student for entire event' : ` booking(s) for ${maxBookingsRestrictedDays} days before event then unlimited`}
        title="Booking Limits (Per Student)"
        stat={maxBookingsPerStudent === 0 ? "Unlimited" : maxBookingsPerStudent }
        handleClick={() => openEventMenu("SETTINGS")}
      />
    </React.Fragment>
  )
}

EventSettings.propTypes = {
  event: PropTypes.object.isRequired,
  maxBookingsPerStudent: PropTypes.number,
  maxBookingsRestrictedDays: PropTypes.number,
  meetingTypes: PropTypes.arrayOf(PropTypes.object),
  noOfSessions: PropTypes.number,
  openToParents: PropTypes.bool,
  sessions: PropTypes.arrayOf(PropTypes.object),
  slotDuration: PropTypes.number,
  slotGap: PropTypes.number,
  user: PropTypes.object.isRequired,
  vcAutoDisconnect: PropTypes.bool,
}

export default EventSettings
